// Browser side of B6 identity workflows (frozen contract: services/identity-workflows/src/contracts.ts,
// docs/implementation/B6-IDENTITY-WORKFLOWS.md). Shapes are mirrored and checked by hand so the
// bundle never loads TypeBox; tests pin these mirrors to the contract file. Every response is
// validated exactly before the UI acts on it. Nothing here logs or stores a value.

export const ROLE_IDS = ['owner', 'admin', 'supervisor', 'agent', 'analyst'] as const;
export type RoleId = (typeof ROLE_IDS)[number];

/** Display names from packages/domain ROLES. */
export const ROLE_LABELS: Readonly<Record<RoleId, string>> = {
  owner: 'Tenant owner',
  admin: 'Administrator',
  supervisor: 'Supervisor',
  agent: 'Agent',
  analyst: 'Analyst',
};

/** A copy of packages/domain ROLE_DELEGATION (a test keeps them equal). The server rechecks every request. */
export const ROLE_DELEGATION: Readonly<Record<string, readonly RoleId[]>> = {
  owner: ['owner', 'admin', 'supervisor', 'agent', 'analyst'],
  admin: ['admin', 'supervisor', 'agent', 'analyst'],
};

/** Same rule as packages/domain mayAssignRoles: one of my roles must cover every requested role. */
export function mayAssignRoles(actorRoles: readonly string[], next: readonly string[]): boolean {
  if (next.length === 0 || new Set(next).size !== next.length || next.some(r => !(ROLE_IDS as readonly string[]).includes(r))) return false;
  return actorRoles.some(role => next.every(target => (ROLE_DELEGATION[role] as readonly string[] | undefined)?.includes(target) ?? false));
}

/** Roles my current membership may invite people into, in canonical order. */
export function delegableRoles(actorRoles: readonly string[]): RoleId[] {
  return ROLE_IDS.filter(role => actorRoles.some(mine => ROLE_DELEGATION[mine]?.includes(role)));
}

export function roleText(roleIds: readonly string[]): string {
  return roleIds.map(r => ROLE_LABELS[r as RoleId] ?? r).join(', ');
}

export const INVITATION_STATES = ['queued', 'provider_accepted', 'delivery_failed', 'accepted', 'revoked', 'expired'] as const;
export type InvitationState = (typeof INVITATION_STATES)[number];

/** Provider acceptance is not delivery: there is no delivered state in B6. */
export const INVITATION_STATE_COPY: Readonly<Record<InvitationState, { label: string; hint: string; tone: '' | 'tone-warn' | 'tone-live' | 'tone-hold' }>> = {
  queued: { label: 'Queued', hint: 'Waiting to be handed to the email provider. Not sent yet.', tone: 'tone-hold' },
  provider_accepted: { label: 'Accepted by email provider', hint: 'The email provider took the message. That isn’t confirmation it reached their inbox.', tone: 'tone-hold' },
  delivery_failed: { label: 'Email failed', hint: 'The email provider didn’t take the message. Revoke this invitation and send a new one.', tone: 'tone-warn' },
  accepted: { label: 'Joined', hint: 'They accepted and joined this workspace.', tone: 'tone-live' },
  revoked: { label: 'Revoked', hint: 'This invitation link no longer works.', tone: '' },
  expired: { label: 'Expired', hint: 'This invitation link no longer works.', tone: '' },
};

/** Invitations that can still be revoked (the server refuses accepted ones). */
export function revocable(state: InvitationState): boolean {
  return state === 'queued' || state === 'provider_accepted' || state === 'delivery_failed';
}

const OPAQUE = /^[A-Za-z0-9_-]{43}$/;
const EMAIL = /^[A-Za-z0-9.!#$%&'*+/=?^_`{|}~-]+@[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?(?:\.[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?)+$/;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const BASE32 = /^[A-Z2-7]{16,128}$/;
export const PASSWORD_MIN = 12;
export const PASSWORD_MAX = 1024;
export const DISPLAY_NAME_MAX = 120;
export const OTP = /^[0-9]{6}$/;

export const isOpaque = (value: unknown): value is string => typeof value === 'string' && OPAQUE.test(value);
export const isEmail = (value: unknown): value is string => typeof value === 'string' && value.length >= 3 && value.length <= 254 && EMAIL.test(value);

/** Where a finished invitation may send the user: exactly `/`, `/team` or a desktop authorization handle. */
export function safeContinuation(value: unknown): string | null {
  if (value === '/' || value === '/team') return value;
  return typeof value === 'string' && /^\/desktop\/authorize\?request=[A-Za-z0-9_-]{43}$/.test(value) ? value : null;
}
export const isDesktopContinuation = (value: string | null): boolean => !!value?.startsWith('/desktop/authorize?');

function plain(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
function keysExactly(value: Record<string, unknown>, required: readonly string[], optional: readonly string[] = []): boolean {
  const own = Object.keys(value);
  return required.every(k => Object.hasOwn(value, k)) && own.every(k => required.includes(k) || optional.includes(k));
}
const isDate = (value: unknown): value is string => typeof value === 'string' && value.length <= 64 && Number.isFinite(Date.parse(value));
const isRoles = (value: unknown): value is RoleId[] =>
  Array.isArray(value) && value.every(r => (ROLE_IDS as readonly unknown[]).includes(r));
const isState = (value: unknown): value is InvitationState => (INVITATION_STATES as readonly unknown[]).includes(value);

// ---- Capabilities ------------------------------------------------------------------------------

export interface IdentityCapability {
  status: 'enabled' | 'unconfigured';
  allowed: boolean;
  reason_code: string | null;
}
export interface IdentityCapabilities {
  invitations: IdentityCapability;
  recovery: IdentityCapability;
  mfa: IdentityCapability;
}

function parseCapability(value: unknown): IdentityCapability | null {
  if (!plain(value) || !keysExactly(value, ['status', 'allowed', 'reason_code'])) return null;
  if ((value.status !== 'enabled' && value.status !== 'unconfigured') || typeof value.allowed !== 'boolean') return null;
  if (value.reason_code !== null && (typeof value.reason_code !== 'string' || value.reason_code.length > 100)) return null;
  return { status: value.status, allowed: value.allowed, reason_code: value.reason_code };
}

export function parseCapabilities(value: unknown): IdentityCapabilities | null {
  if (!plain(value) || !keysExactly(value, ['invitations', 'recovery', 'mfa'])) return null;
  const invitations = parseCapability(value.invitations);
  const recovery = parseCapability(value.recovery);
  const mfa = parseCapability(value.mfa);
  return invitations && recovery && mfa ? { invitations, recovery, mfa } : null;
}

// ---- Invitation acceptance ---------------------------------------------------------------------

export interface InviteInspect {
  tenant_name: string;
  email: string;
  role_ids: RoleId[];
  expires_at: string;
  /** Allowlisted, or null when the server sent anything else (never followed). */
  continue_path: string | null;
}

export function parseInspect(value: unknown): InviteInspect | null {
  if (!plain(value) || !keysExactly(value, ['tenant_name', 'email', 'role_ids', 'expires_at', 'continue_path'])) return null;
  if (typeof value.tenant_name !== 'string' || !value.tenant_name.trim() || value.tenant_name.length > 200) return null;
  if (!isEmail(value.email) || !isRoles(value.role_ids) || value.role_ids.length === 0 || !isDate(value.expires_at) || typeof value.continue_path !== 'string') return null;
  return { tenant_name: value.tenant_name, email: value.email, role_ids: value.role_ids, expires_at: value.expires_at, continue_path: safeContinuation(value.continue_path) };
}

export interface PreparedMfa {
  required: boolean;
  enrollment_required: boolean;
  /** Only for a pending enrollment; shown only in the enrollment view. */
  secret: string | null;
  account: string | null;
}
export interface Prepared {
  challenge: string;
  expires_at: string;
  mfa: PreparedMfa;
}

/** `otpauth://totp/<label>?secret=…&issuer=…`: the secret must match; returns the decoded account label. */
function otpauthAccount(uri: unknown, secret: string): string | null {
  if (typeof uri !== 'string' || uri.length > 1024 || !uri.startsWith('otpauth://totp/') || /[\s#]/.test(uri)) return null;
  const at = uri.indexOf('?');
  if (at < 0) return null;
  const seen = new Map<string, string>();
  for (const part of uri.slice(at + 1).split('&')) {
    const eq = part.indexOf('=');
    if (eq <= 0) return null;
    const key = part.slice(0, eq);
    if (seen.has(key)) return null;
    let decoded: string;
    try {
      decoded = decodeURIComponent(part.slice(eq + 1));
    } catch {
      return null;
    }
    seen.set(key, decoded);
  }
  if (seen.get('secret') !== secret) return null;
  try {
    const label = decodeURIComponent(uri.slice('otpauth://totp/'.length, at));
    return label.length > 0 && label.length <= 300 ? label : null;
  } catch {
    return null;
  }
}

export function parsePrepared(value: unknown): Prepared | null {
  if (!plain(value) || !keysExactly(value, ['challenge', 'expires_at', 'mfa'])) return null;
  if (!isOpaque(value.challenge) || !isDate(value.expires_at)) return null;
  const mfa = value.mfa;
  if (!plain(mfa) || !keysExactly(mfa, ['required', 'enrollment_required'], ['secret', 'otpauth_uri'])) return null;
  if (typeof mfa.required !== 'boolean' || typeof mfa.enrollment_required !== 'boolean') return null;
  const hasSeed = mfa.secret !== undefined || mfa.otpauth_uri !== undefined;
  if (!mfa.enrollment_required) {
    // A seed outside a pending enrollment is never displayed.
    if (hasSeed) return null;
    return { challenge: value.challenge, expires_at: value.expires_at, mfa: { required: mfa.required, enrollment_required: false, secret: null, account: null } };
  }
  if (!mfa.required || typeof mfa.secret !== 'string' || !BASE32.test(mfa.secret)) return null;
  const account = otpauthAccount(mfa.otpauth_uri, mfa.secret);
  if (account === null) return null;
  return { challenge: value.challenge, expires_at: value.expires_at, mfa: { required: true, enrollment_required: true, secret: mfa.secret, account } };
}

export interface InviteAccepted {
  /** Allowlisted continuation; desktop handles are never resumed (B6 returns `/` for them). */
  continue_path: string;
}

export function parseAccepted(value: unknown): InviteAccepted | null {
  if (!plain(value) || !keysExactly(value, ['accepted', 'sign_in_required', 'continue_path'])) return null;
  if (value.accepted !== true || value.sign_in_required !== true) return null;
  const next = safeContinuation(value.continue_path);
  if (next === null) return null;
  return { continue_path: isDesktopContinuation(next) ? '/' : next };
}

// ---- Recovery and MFA enrollment ---------------------------------------------------------------

export function parseRecoveryRequested(value: unknown): boolean {
  return plain(value) && keysExactly(value, ['status', 'detail']) && value.status === 'accepted' && typeof value.detail === 'string';
}
export function parseReset(value: unknown): boolean {
  return plain(value) && keysExactly(value, ['reset', 'sign_in_required']) && value.reset === true && value.sign_in_required === true;
}
export function parseEnrolled(value: unknown): boolean {
  return plain(value) && keysExactly(value, ['enrolled', 'sign_in_required']) && value.enrolled === true && value.sign_in_required === true;
}

// ---- Team invitations --------------------------------------------------------------------------

export interface InvitationView {
  id: string;
  email: string;
  role_ids: RoleId[];
  state: InvitationState;
  expires_at: string;
  created_at: string;
}

export function parseInvitation(value: unknown): InvitationView | null {
  if (!plain(value) || !keysExactly(value, ['id', 'email', 'role_ids', 'state', 'expires_at', 'created_at'])) return null;
  if (typeof value.id !== 'string' || !UUID.test(value.id) || !isEmail(value.email) || !isRoles(value.role_ids) || !isState(value.state)) return null;
  if (!isDate(value.expires_at) || !isDate(value.created_at)) return null;
  return { id: value.id, email: value.email, role_ids: value.role_ids, state: value.state, expires_at: value.expires_at, created_at: value.created_at };
}

export interface InvitationPage {
  items: InvitationView[];
  next_cursor: string | null;
}

/** The whole collection envelope: `{data, page:{next_cursor}, meta:{request_id}}`. */
export function parseInvitationPage(envelope: unknown): InvitationPage | null {
  if (!plain(envelope) || !keysExactly(envelope, ['data', 'page', 'meta']) || !Array.isArray(envelope.data) || !plain(envelope.page)) return null;
  const next = envelope.page.next_cursor;
  if (!keysExactly(envelope.page, ['next_cursor']) || (next !== null && (typeof next !== 'string' || next.length === 0 || next.length > 2048))) return null;
  const items: InvitationView[] = [];
  for (const row of envelope.data) {
    const item = parseInvitation(row);
    if (!item) return null;
    items.push(item);
  }
  return { items, next_cursor: next };
}
